import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage } from "../services/expensesService";
import { fetchStatementFile, requestStatementCode, StatementFileError, unlockStatements } from "../services/statementVaultService";
import { ui } from "../theme";
import { Dialog, ErrorBox, Field, Spinner } from "./ui";

// ---------------------------------------------------------------------------
// Visor de un estado de cuenta cifrado (#1029). El archivo solo se descifra en
// el servidor para un dueño con código vigente: si el servidor responde 423,
// se pide el código que llega al correo y, con él, se abre aquí mismo. El PDF
// vive en memoria (URL de objeto) y se descarta al cerrar.
// ---------------------------------------------------------------------------

type Step = "loading" | "needCode" | "codeSent" | "ready" | "error";

export default function StatementViewer({ statementId, label, onClose }: { statementId: string | null; label?: string | null; onClose: () => void }) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [step, setStep] = useState<Step>("loading");
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<{ url: string; type: string } | null>(null);
  const urlRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!statementId) return;
    setError(null);
    setStep("loading");
    try {
      const blob = await fetchStatementFile(statementId);
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setFile({ url, type: blob.type });
      setStep("ready");
    } catch (err) {
      if (err instanceof StatementFileError && err.status === 423) {
        setStep("needCode");
        return;
      }
      setError((err as Error).message || t("expenses.statements.errors.open"));
      setStep("error");
    }
  }, [statementId, t]);

  useEffect(() => {
    void load();
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    };
  }, [load]);

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await requestStatementCode();
      setSentTo(r.sentTo);
      setStep("codeSent");
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.statements.errors.code"));
    } finally {
      setBusy(false);
    }
  };

  const unlock = async () => {
    setBusy(true);
    setError(null);
    try {
      await unlockStatements(code);
      setCode("");
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.statements.errors.unlock"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!statementId} wide title={label || t("expenses.statements.title")} onClose={onClose}>
      <div className="space-y-3">
        <ErrorBox message={error} />
        {step === "loading" && <Spinner label={t("expenses.statements.opening")} />}
        {(step === "needCode" || step === "codeSent") && (
          <div className={`space-y-3 rounded-xl p-4 ${c.surface}`}>
            <p className={`flex items-center gap-2 text-sm font-medium ${c.text}`}>
              <i className="ri-lock-2-line text-lg text-teal-600" />
              {t("expenses.statements.locked")}
            </p>
            <p className={`text-sm ${c.muted}`}>{t("expenses.statements.lockedHint")}</p>
            {step === "needCode" ? (
              <button type="button" className={c.primary} onClick={sendCode} disabled={busy}>
                {t("expenses.statements.sendCode")}
              </button>
            ) : (
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void unlock();
                }}
              >
                <Field label={t("expenses.statements.codeLabel", { email: sentTo || "" })}>
                  <input
                    className={`${c.inputBase} w-40 text-center font-mono tracking-[0.4em]`}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    aria-label={t("expenses.statements.codeAria")}
                    autoFocus
                  />
                </Field>
                <button type="submit" className={c.primary} disabled={busy || code.length !== 6}>
                  {t("expenses.statements.unlock")}
                </button>
                <button type="button" className={c.secondary} onClick={sendCode} disabled={busy}>
                  {t("expenses.statements.resend")}
                </button>
              </form>
            )}
            {busy && <Spinner />}
          </div>
        )}
        {step === "ready" && file && (
          <div className="space-y-2">
            {file.type.startsWith("image/") ? (
              <img src={file.url} alt={label || ""} className="max-h-[70vh] w-full rounded-lg object-contain" />
            ) : (
              <iframe src={file.url} title={label || t("expenses.statements.title")} className="h-[70vh] w-full rounded-lg border-0" />
            )}
            <p className={`text-xs ${c.muted}`}>{t("expenses.statements.auditNote")}</p>
          </div>
        )}
      </div>
    </Dialog>
  );
}
